import type { SVG_NAMES } from '~/components/icons'
import { dapLangGlobal } from '#imports'

export interface PalletList {
  pallets: Pallet[]
}

export interface Pallet {
  palletId: string
  parts: CurationPartDefinition[]
  add?: (i) => void
}

export type CES_PAGE =
  | 'mynote'
  | 'workspace'
  | 'gallery'
  | 'mygallery'
  | 'page'
  | 'organization'
  | 'database'
export type CES_MODE = 'note' | 'full'

export interface CurationEditState {
  canUndo: boolean
  canRedo: boolean
  canSave: boolean
  isSaving: boolean
  curation: Curation
  page?: CES_PAGE
  mode?: CES_MODE
  undo(): void
  redo(): void
  save(): Promise<void>
  snapshot(): void
  focusing?: string
  editors?: { [key: string]: WorkspaceUser }
}

const curationPartsName: { [key: string]: any } = {
  'jps-curation-image': {
    ja: '画像パーツ',
    en: 'Image',
    icon: 'mdi-image',
    description: {
      ja: 'キャプションを付けることができる画像',
      en: 'Image with caption',
    },
  },
  'jps-curation-image-annotation': {
    ja: '解説付き画像パーツ',
    en: 'Annotated Image',
    icon: 'mdi-image-edit-outline',
    description: {
      ja: '解説を付けることができる画像',
      en: 'Image which can be annotated',
    },
  },
  'jps-curation-area-annotation': {
    ja: '解説',
    en: 'Annotation',
    icon: 'mdi-message-processing-outline',
  },
  'jps-curation-timeline': {
    ja: '年表パーツ',
    en: 'Timeline',
    icon: 'mdi-timeline-text',
    description: {
      ja: '時系列にそって解説を作成できる',
      en: 'Create explanations in chronological order',
    },
  },
  'jps-curation-timepoint': {
    ja: '時点',
    en: 'Timepoint',
    icon: 'mdi-clock-outline',
  },
  'jps-curation-section': {
    ja: 'セクションパーツ',
    en: 'Section',
    icon: 'mdi-format-section',
    description: {
      ja: 'セクションを切って見出しを付けることができる',
      en: 'Section with a title',
    },
  },
  'jps-curation-search-box': {
    ja: '検索ボックスパーツ',
    en: 'Search Box',
    icon: 'mdi-card-search',
    description: {
      ja: 'カスタマイズ可能な検索ボックス',
      en: 'Customizable search box',
    },
  },
  'jps-curation-text': {
    ja: 'テキストパーツ',
    en: 'Text',
    icon: 'mdi-text-box',
    description: {
      ja: '書式設定が可能な本文',
      en: 'Formatted text',
    },
  },
  'jps-curation-list': {
    ja: 'リストパーツ',
    en: 'List',
    icon: 'mdi-view-list',
    description: {
      ja: 'メタデータなどを様々な形式で並べることができる',
      en: 'Display items in various layouts',
    },
  },
  'jps-curation-list-item': {
    ja: 'メタデータ',
    en: 'Metadata',
    icon: 'mdi-book-information-variant',
    description: {
      ja: 'メタデータを選択してリストに追加する',
      en: 'Add metadata to the list',
    },
  },
  'jps-curation-list-curation': {
    ja: 'ギャラリー',
    en: 'Gallery',
    icon: 'mdi-image-auto-adjust',
    description: {
      ja: '公開されているギャラリーをリストに追加する',
      en: 'Add galleries published on Japan Search to the list',
    },
  },
  'jps-curation-list-search-result': {
    ja: '検索結果',
    en: 'Search Result',
    icon: 'mdi-magnify-plus',
    description: {
      ja: '検索結果をリストに追加する',
      en: 'Add search results to the list',
    },
  },
  'jps-curation-list-csearch': {
    ja: 'テーマ別検索',
    en: 'Designed Search',
    icon: 'mdi-magnify',
    description: {
      ja: '公開されているテーマ別検索をリストに追加する',
      en: 'Add designed searches to the list',
    },
  },
  'jps-curation-list-external-link': {
    ja: '外部リンク',
    en: 'External Link',
    icon: 'mdi-open-in-new',
    description: {
      ja: '外部リンクをリストに追加する。リンクを設定しなければ、単純なテキスト項目として利用できる',
      en: 'Add external links to the list. This can be used as a simple text if no link is set.',
    },
  },
  'jps-curation-list-database': {
    ja: 'データベース',
    en: 'Database',
    icon: 'mdi-cloud-circle',
    description: {
      ja: '公開されているデータベースをリストに追加する',
      en: 'Add databaces published on Japan Search to the list',
    },
  },
  'jps-curation-list-organization': {
    ja: '連携機関',
    en: 'Partners',
    icon: 'mdi-link-box-variant-outline',
    description: {
      ja: '公開されている連携機関やプロジェクトをリストに追加する',
      en: 'Add Japan Search partners or projects information to the list',
    },
  },
  'jps-curation-subpage-list': {
    ja: '子ページ目次',
    en: 'Child Page Index',
    icon: 'mdi-format-list-bulleted-square',
    description: {
      ja: '子ページの目次を表示する',
      en: 'Index for child page',
    },
  },
  'jps-curation-index': {
    ja: '目次パーツ',
    en: 'Table of Contents',
    icon: 'mdi-format-list-bulleted-type',
    description: {
      ja: 'このページの目次を表示する',
      en: 'Show table of contents for this page',
    },
  },
  'jps-curation-iframe': {
    ja: '外部サイト埋め込みパーツ',
    en: 'Embedded External Site',
    icon: 'mdi-format-list-bulleted-square',
    description: {
      ja: 'iframeを利用した外部サイトの埋め込み',
      en: 'Embed external site using iframe',
    },
  },
  'jps-curation-tab': {
    ja: 'タブパーツ',
    en: 'Tab',
    icon: 'mdi-tab',
    description: {
      ja: 'タブを使ってコンテンツを配置',
      en: 'Display items using tabs',
    },
  },
  'jps-curation-tabcontent': {
    ja: 'タブコンテンツ',
    en: 'Tab contents',
    icon: 'mdi-tab-plus',
  },
}

export const useCurationEditor = () => {

  const { ls, li } = useI18n()
  const dataStore = useDataStore()
  const { $getCurationThemes } = useNuxtApp()

  const getPartTitle = (
    def: CurationPartDefinition,
    withName = false
  ): string => {
    let partName = withName ? ls(getCurationPartsName(def)) + ' ' : ''
    switch (def.type) {
      case 'jps-curation-text':
        if (def.ja && def.ja.ops.length > 0 && def.ja.ops[0].insert.length > 1)
          return partName + abb(def.ja.ops[0].insert, 30)
        break
      case 'jps-curation-image':
        if (ls(def.caption)) return partName + ls(def.caption)
        break
      case 'jps-curation-search-box':
        if (def.csId && dataStore.csearch(def.csId))
          return partName + ls(dataStore.csearch(def.csId).name)
        break
      case 'jps-curation-timeline':
        break
      case 'jps-curation-timepoint':
        if (def.time && ls(def.time.name))
          return partName + ls(def.time.name)
        break
      case 'jps-curation-tab':
        break
      case 'jps-curation-tabcontent':
        if (def.title && ls(def.title)) return partName + ls(def.title)
        break
      case 'jps-curation-image-annotation':
        if (def.image && !isEmpty(def.image.title))
          return partName + ls(def.image.title)
        break
      case 'jps-curation-area-annotation':
        return partName + ls(def.title)
        break
      case 'jps-curation-section':
        if (ls(def.title)) return partName + ls(def.title)
        break
      case 'jps-curation-list':
        if (ls(def.title)) return partName + ls(def.title)
        break
      case 'jps-curation-list-item':
        if (def.id && dataStore.item(def.id))
          return partName + abb(li(dataStore.item(def.id)), 30)
        break
      case 'jps-curation-list-curation':
        if (def.id && dataStore.curation(def.id))
          return (
            partName + abb(ls(dataStore.curation(def.id).title), 30)
          )
        break
      case 'jps-curation-list-csearch':
        if (def.id && dataStore.csearch(def.id))
          return (
            partName + abb(ls(dataStore.csearch(def.id).name), 30)
          )
        break
      case 'jps-curation-list-database':
        if (def.id && dataStore.database(def.id))
          return (
            partName + abb(ls(dataStore.database(def.id).name), 30)
          )
        break
      case 'jps-curation-list-external-link':
        if (ls(def.title)) return partName + abb(ls(def.title), 30)
        break
      case 'jps-curation-list-organization':
        if (def.id && dataStore.organization(def.id))
          return (
            partName +
            abb(ls(dataStore.organization(def.id).name), 30)
          )
        break
      case 'jps-curation-list-search-result':
        if (ls(def.title)) return partName + ls(def.title)
        break
      case 'jps-curation-subpage-list':
        break
    }
    return ls(getCurationPartsName(def))
  }

  const getCurationPartsName = (def: CurationPartDefinition): LangString => {
    return curationPartsName[def.type]
  }

  const getCurationPartsIcon = (def: CurationPartDefinition): SVG_NAMES => {
    return curationPartsName[def.type].icon
  }

  const getCurationPartsDescription = (def: CurationPartDefinition): LangString => {
    return curationPartsName[def.type].description
  }

  const getCurationThemes = () => {
    return $getCurationThemes()
  }

  return {
    getPartTitle,
    getCurationPartsName,
    getCurationPartsIcon,
    getCurationPartsDescription,
    getCurationThemes,
  }
}

